import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent
} from "react";
import { api } from "../api";
import type {
  LicenseEvent,
  LicenseInstallation,
  LicenseSku,
  LicenseTrialIp,
  LocalLicenseStatus,
  PackagePlan,
  PanelLicense
} from "../types";

type Summary = {
  total: number;
  trial: number;
  active: number;
  expired: number;
  revoked: number;
  uniqueIps: number;
  trialIps: number;
};

type MainTab = "ips" | "licenses" | "packages" | "events" | "local";
type StatusFilter =
  | "all"
  | "trial"
  | "active"
  | "expired"
  | "suspended"
  | "unassigned";

function daysLeft(expiresAt?: string | null): number | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function maskKey(key?: string | null): string {
  if (!key) return "—";
  if (key.length < 12) return "RP-••••";
  return `${key.slice(0, 3)}-••••-••••-${key.slice(-4)}`;
}

function installStatus(i: LicenseInstallation): {
  key: string;
  label: string;
  cls: string;
} {
  const left = daysLeft(i.expiresAt);
  const s = String(i.status || "").toLowerCase();
  if (s === "revoked" || s === "blocked")
    return { key: "suspended", label: "SUSPENDED", cls: "lic-badge-danger" };
  if (s === "expired" || (left != null && left < 0))
    return { key: "expired", label: "EXPIRED", cls: "lic-badge-danger" };
  if (s === "trial")
    return { key: "trial", label: "TRIAL", cls: "lic-badge-warn" };
  if (left != null && left <= 14)
    return { key: "expiring", label: "EXPIRING SOON", cls: "lic-badge-warn" };
  if (s === "active")
    return { key: "active", label: "ACTIVE", cls: "lic-badge-ok" };
  return { key: s || "unknown", label: (s || "UNKNOWN").toUpperCase(), cls: "lic-badge-muted" };
}

/**
 * Premium Super Admin licensing console — Server IPs / installations registry.
 * Spec: licenses_server_ips_redesign_upgrade.md
 */
export function LicensesPage({ embedded }: { embedded?: boolean } = {}) {
  const [tab, setTab] = useState<MainTab>("ips");
  const [items, setItems] = useState<PanelLicense[]>([]);
  const [installs, setInstalls] = useState<LicenseInstallation[]>([]);
  const [trialIps, setTrialIps] = useState<LicenseTrialIp[]>([]);
  const [events, setEvents] = useState<LicenseEvent[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [local, setLocal] = useState<LocalLicenseStatus | null>(null);
  const [error, setError] = useState("");
  const [errorDetail, setErrorDetail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [issuedKey, setIssuedKey] = useState("");
  const [revealedKey, setRevealedKey] = useState(false);
  const [toast, setToast] = useState("");

  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [days, setDays] = useState(365);
  const [maxServers, setMaxServers] = useState(1);
  const [plan] = useState<"standard" | "business" | "unlimited">("standard");
  const [notes, setNotes] = useState("");
  const [maxAccounts, setMaxAccounts] = useState(10);
  const [kind, setKind] = useState<"user" | "reseller">("user");
  const [skuId, setSkuId] = useState("");
  const [skus, setSkus] = useState<LicenseSku[]>([]);
  const [ownerPackages, setOwnerPackages] = useState<PackagePlan[]>([]);

  const [assignId, setAssignId] = useState<string | null>(null);
  const [assignPackageId, setAssignPackageId] = useState("");
  const [assignDays, setAssignDays] = useState(365);
  const [assignCustomer, setAssignCustomer] = useState("");
  const [assignEmail, setAssignEmail] = useState("");
  const [detail, setDetail] = useState<LicenseInstallation | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PanelLicense | null>(null);

  const [applyKey, setApplyKey] = useState("");
  const [renewDays, setRenewDays] = useState<Record<string, number>>({});
  const [trialDays, setTrialDays] = useState(7);
  const [trialDaysDraft, setTrialDaysDraft] = useState(7);
  const [trialSaveBusy, setTrialSaveBusy] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<"all" | "vps" | "reseller">(
    "all"
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [ipSearch, setIpSearch] = useState("");

  const vpsPackages = useMemo(
    () =>
      ownerPackages.filter(
        (p) => (p.planKind || "hosting") === "hosting" && !p.ownerResellerId
      ),
    [ownerPackages]
  );
  const resellerPackages = useMemo(
    () =>
      ownerPackages.filter(
        (p) => p.planKind === "reseller" && !p.ownerResellerId
      ),
    [ownerPackages]
  );

  const showToast = (t: string) => {
    setToast(t);
    window.setTimeout(() => setToast(""), 2800);
  };

  const load = useCallback(async () => {
    setError("");
    setErrorDetail("");
    setLoading(true);
    try {
      const [list, inst, tips, ev, skuRes, pkgRes, trialSet] =
        await Promise.all([
          api<{
            ok: boolean;
            items: PanelLicense[];
            summary: Summary;
            local: LocalLicenseStatus;
          }>("/api/v1/licenses"),
          api<{ ok: boolean; items: LicenseInstallation[] }>(
            "/api/v1/licenses/installations"
          ),
          api<{ ok: boolean; items: LicenseTrialIp[] }>(
            "/api/v1/licenses/trial-ips"
          ),
          api<{ ok: boolean; items: LicenseEvent[] }>(
            "/api/v1/licenses/events?limit=80"
          ),
          api<{ ok: boolean; items: LicenseSku[] }>(
            "/api/v1/license/skus?all=1"
          ).catch(() => ({ ok: true, items: [] as LicenseSku[] })),
          api<{ ok: boolean; items: PackagePlan[] }>("/api/v1/packages").catch(
            () => ({ ok: true, items: [] as PackagePlan[] })
          ),
          api<{
            ok: boolean;
            settings: { defaultTrialDays: number };
          }>("/api/v1/license/trial-settings").catch(() => ({
            ok: true,
            settings: { defaultTrialDays: 7 }
          }))
        ]);
      setItems(list.items || []);
      setSummary(list.summary || null);
      setLocal(list.local || null);
      setInstalls(inst.items || []);
      setTrialIps(tips.items || []);
      setEvents(ev.items || []);
      setSkus(skuRes.items || []);
      setOwnerPackages(pkgRes.items || []);
      const d = Number(trialSet.settings?.defaultTrialDays || 7);
      setTrialDays(d);
      setTrialDaysDraft(d);
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      setError("The licensing registry could not be loaded.");
      setErrorDetail(m);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Deep-link: ?ip= or ?install_id=
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const ip = sp.get("ip");
      const installId = sp.get("install_id");
      if (!installs.length) return;
      if (ip) {
        const hit = installs.find((i) => i.serverIp === ip);
        if (hit) {
          setTab("ips");
          setDetail(hit);
        }
      } else if (installId) {
        const hit = installs.find((i) => i.installId === installId);
        if (hit) {
          setTab("ips");
          setDetail(hit);
        }
      }
    } catch {
      /* ignore */
    }
  }, [installs]);

  const saveTrialSettings = async (e: FormEvent) => {
    e.preventDefault();
    setTrialSaveBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{
        ok: boolean;
        settings: { defaultTrialDays: number; note?: string };
      }>("/api/v1/license/trial-settings", {
        method: "PUT",
        body: JSON.stringify({ defaultTrialDays: trialDaysDraft })
      });
      const d = Number(res.settings?.defaultTrialDays || trialDaysDraft);
      setTrialDays(d);
      setTrialDaysDraft(d);
      setMsg(
        `Trial policy updated. New installations will receive a ${d}-day free trial.`
      );
      showToast("Trial policy updated");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setTrialSaveBusy(false);
    }
  };

  const onIssue = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    setIssuedKey("");
    setRevealedKey(false);
    try {
      const res = await api<{ ok: boolean; license: PanelLicense }>(
        "/api/v1/licenses",
        {
          method: "POST",
          body: JSON.stringify({
            customerName,
            customerEmail,
            days,
            plan,
            maxServers,
            maxAccounts,
            kind,
            skuId: skuId || undefined,
            notes
          })
        }
      );
      setIssuedKey(res.license.licenseKey);
      setMsg(
        `License issued for ${res.license.customerName} · expires ${formatDate(res.license.expiresAt)}`
      );
      setCustomerName("");
      setCustomerEmail("");
      setNotes("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRenew = async (id: string) => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const d = renewDays[id] ?? 30;
      const res = await api<{ ok: boolean; license: PanelLicense }>(
        `/api/v1/licenses/${id}/renew`,
        {
          method: "POST",
          body: JSON.stringify({ days: d })
        }
      );
      setMsg(
        `Renewed ${res.license.customerName || "license"} → ${formatDate(res.license.expiresAt)} (+${d}d)`
      );
      showToast("License renewed");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRevokeConfirm = async () => {
    if (!revokeTarget) return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/v1/licenses/${revokeTarget.id}/revoke`, {
        method: "POST",
        body: "{}"
      });
      setMsg("License revoked");
      showToast("License revoked");
      setRevokeTarget(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onApplyKey = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{ ok: boolean; message?: string; valid?: boolean }>(
        "/api/v1/license/apply-key",
        {
          method: "POST",
          body: JSON.stringify({ licenseKey: applyKey })
        }
      );
      setMsg(res.message || (res.ok ? "Key applied" : "Failed"));
      setApplyKey("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (id: string) => {
    setMenuId(null);
    setBusy(true);
    try {
      const res = await api<{ ok: boolean; installation: LicenseInstallation }>(
        `/api/v1/licenses/installations/${id}`
      );
      setDetail(res.installation);
    } catch (err) {
      // Fall back to list row data
      const row = installs.find((x) => x.id === id) || null;
      setDetail(row);
      if (!row) setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const openAssign = (i: LicenseInstallation) => {
    setMenuId(null);
    setAssignId(i.id);
    setAssignCustomer(i.ownerName || i.customerName || i.hostname || "");
    setAssignEmail(i.ownerEmail || i.customerEmail || "");
    setAssignDays(365);
    const prefer =
      i.packageId ||
      (i.kind === "reseller"
        ? resellerPackages[0]?.id
        : vpsPackages[0]?.id) ||
      ownerPackages[0]?.id ||
      "";
    setAssignPackageId(prefer);
    setMsg("");
    setError("");
  };

  const onAssignPackage = async (e: FormEvent) => {
    e.preventDefault();
    if (!assignId || !assignPackageId) {
      setError("Select a VPS or Reseller package");
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    setRevealedKey(false);
    try {
      const res = await api<{
        ok: boolean;
        licenseKey: string;
        license: PanelLicense;
        installation: LicenseInstallation;
      }>(`/api/v1/licenses/installations/${assignId}/assign-package`, {
        method: "POST",
        body: JSON.stringify({
          packageId: assignPackageId,
          days: assignDays,
          customerName: assignCustomer || undefined,
          customerEmail: assignEmail || undefined
        })
      });
      setIssuedKey(res.licenseKey);
      const pkgName = res.license.packageName || "package";
      const owner =
        res.installation?.ownerName ||
        res.installation?.customerName ||
        res.installation?.serverIp ||
        "";
      setMsg(`Package assigned. ${pkgName} is now active for ${owner}.`);
      showToast("Package assigned");
      setAssignId(null);
      setDetail(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(label);
    } catch {
      showToast("Copy failed");
    }
  };

  const filteredInstalls = useMemo(() => {
    let list = installs;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((i) => {
        const blob = [
          i.ownerName,
          i.customerName,
          i.ownerEmail,
          i.customerEmail,
          i.serverIp,
          i.hostname,
          i.installId,
          i.packageName,
          i.status
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(q);
      });
    }
    if (statusFilter !== "all") {
      list = list.filter((i) => {
        const st = installStatus(i);
        if (statusFilter === "unassigned") return !i.packageId;
        if (statusFilter === "suspended")
          return st.key === "suspended" || i.status === "blocked";
        // st.key may be "expiring" (derived) while statusFilter is a coarse status
        if (statusFilter === "active")
          return st.key === "active" || st.key === "expiring";
        return st.key === statusFilter || i.status === statusFilter;
      });
    }
    if (typeFilter === "vps") {
      list = list.filter(
        (i) => i.typeLabel === "VPS" || i.kind === "user" || (!i.kind && !i.typeLabel)
      );
    }
    if (typeFilter === "reseller") {
      list = list.filter(
        (i) => i.typeLabel === "Reseller" || i.kind === "reseller"
      );
    }
    return list;
  }, [installs, search, statusFilter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredInstalls.length / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filteredInstalls.slice(
    (pageSafe - 1) * pageSize,
    pageSafe * pageSize
  );

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, typeFilter, pageSize]);

  const selectedPkg = ownerPackages.find((p) => p.id === assignPackageId);
  const assignInstall = installs.find((x) => x.id === assignId);

  const ipDupes = useMemo(() => {
    const map = new Map<string, number>();
    for (const i of installs) {
      map.set(i.serverIp, (map.get(i.serverIp) || 0) + 1);
    }
    return map;
  }, [installs]);

  const filteredTrialIps = useMemo(() => {
    const q = ipSearch.trim().toLowerCase();
    if (!q) return trialIps;
    return trialIps.filter(
      (t) =>
        t.ip.toLowerCase().includes(q) ||
        (t.hostname || "").toLowerCase().includes(q) ||
        (t.notes || "").toLowerCase().includes(q)
    );
  }, [trialIps, ipSearch]);

  const localLeft = daysLeft(local?.expiresAt);
  const localStatus = local
    ? installStatus({
        id: "local",
        licenseId: local.licenseId,
        serverIp: local.serverIp || "",
        hostname: local.hostname,
        machineId: null,
        installId: local.installId,
        status: local.status as LicenseInstallation["status"],
        firstSeenAt: "",
        lastSeenAt: "",
        lastVersion: null,
        expiresAt: local.expiresAt
      })
    : null;

  return (
    <div className="lic-page">
      {!embedded ? (
        <header className="lic-header">
          <div>
            <div className="lic-crumb">Home / Advanced / Licenses</div>
            <h1 className="lic-title">Licenses / Server IPs</h1>
            <p className="lic-sub">
              Manage RealPanel installations, licenses, trial periods and package
              assignments.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void load()}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </header>
      ) : (
        <div className="lic-header lic-header-embed">
          <div>
            <h2 className="lic-section-title" style={{ margin: 0 }}>
              Licenses / Server IPs
            </h2>
            <p className="lic-sub" style={{ margin: "4px 0 0" }}>
              Installations, trial policy and package assignments.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void load()}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      )}

      {toast ? <div className="lic-toast">{toast}</div> : null}
      {msg ? <div className="lic-alert lic-alert-ok">{msg}</div> : null}
      {error ? (
        <div className="lic-alert lic-alert-danger" role="alert">
          <strong>Unable to load license data</strong>
          <span>{error}</span>
          {errorDetail ? (
            <details className="lic-err-detail">
              <summary>View details</summary>
              <code>{errorDetail}</code>
            </details>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}
      {issuedKey ? (
        <div className="lic-alert lic-alert-ok lic-key-banner">
          <div>
            <strong>License key</strong>
            <div className="mono lic-key-val">
              {revealedKey ? issuedKey : maskKey(issuedKey)}
            </div>
          </div>
          <div className="lic-key-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setRevealedKey((v) => !v)}
            >
              {revealedKey ? "Hide" : "Reveal"}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => void copyText(issuedKey, "License key copied.")}
            >
              Copy
            </button>
          </div>
        </div>
      ) : null}

      {/* KPI strip */}
      <div className="lic-kpi-strip">
        {(
          [
            {
              label: "Total Licenses",
              value: summary?.total ?? "—",
              sub: "All registered installations"
            },
            {
              label: "Trial",
              value: summary?.trial ?? "—",
              sub: "Currently in trial"
            },
            {
              label: "Active",
              value: summary?.active ?? "—",
              sub: "Paid / licensed"
            },
            {
              label: "Expired",
              value: summary?.expired ?? "—",
              sub: "Requires attention"
            },
            {
              label: "Recorded IPs",
              value: summary?.trialIps ?? summary?.uniqueIps ?? "—",
              sub: "Unique server IPs"
            }
          ] as const
        ).map((k) => (
          <div key={k.label} className="lic-kpi">
            <span className="lic-kpi-label">{k.label}</span>
            <span className="lic-kpi-value">
              {loading && summary == null ? "…" : k.value}
            </span>
            <span className="lic-kpi-sub">{k.sub}</span>
          </div>
        ))}
      </div>

      {/* Trial policy + this server */}
      <div className="lic-split">
        <section className="lic-card">
          <h3 className="lic-card-title">Trial Policy</h3>
          <p className="lic-card-desc">
            Controls the default free-trial duration for new panel installations.
          </p>
          <form
            className="lic-trial-form"
            onSubmit={(e) => void saveTrialSettings(e)}
          >
            <label className="lic-field">
              <span>Default free trial</span>
              <div className="lic-trial-row">
                <input
                  type="number"
                  min={1}
                  max={3650}
                  className="mono"
                  value={
                    Number.isFinite(trialDaysDraft) && trialDaysDraft > 0
                      ? trialDaysDraft
                      : trialDays
                  }
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setTrialDaysDraft(Number.isFinite(n) ? n : trialDays);
                  }}
                  disabled={trialSaveBusy}
                />
                <span className="lic-unit">days</span>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={
                    trialSaveBusy ||
                    !Number.isFinite(trialDaysDraft) ||
                    trialDaysDraft < 1
                  }
                >
                  {trialSaveBusy ? "Saving…" : "Save"}
                </button>
              </div>
            </label>
            <div className="lic-policy-notes">
              <div>
                Current policy: New installations →{" "}
                <strong>{trialDays} days</strong>
              </div>
              <div className="faint">Existing trials → unchanged</div>
            </div>
          </form>
        </section>

        <section className="lic-card">
          <div className="lic-card-hd-row">
            <h3 className="lic-card-title">This Server</h3>
            {localStatus ? (
              <span className={`lic-badge ${localStatus.cls}`}>
                ● {localStatus.label}
              </span>
            ) : null}
          </div>
          {local ? (
            <>
              <p className="lic-card-desc">
                {local.valid
                  ? `Licensed until ${formatDate(local.expiresAt)}`
                  : local.message || "Not licensed"}
                {local.inGrace ? " · grace period" : ""}
              </p>
              <dl className="lic-meta-grid">
                <div>
                  <dt>Server IP</dt>
                  <dd className="mono">
                    {local.serverIp || "—"}
                    {local.serverIp ? (
                      <button
                        type="button"
                        className="lic-copy"
                        title="Copy IP"
                        onClick={() =>
                          void copyText(local.serverIp!, "Server IP copied.")
                        }
                      >
                        ⎘
                      </button>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt>Install ID</dt>
                  <dd className="mono" title={local.installId}>
                    {local.installId.slice(0, 12)}…
                  </dd>
                </div>
                <div>
                  <dt>Hostname</dt>
                  <dd>{local.hostname || "—"}</dd>
                </div>
                <div>
                  <dt>Expires</dt>
                  <dd>
                    {formatDate(local.expiresAt)}
                    {localLeft != null ? (
                      <span className="faint">
                        {" "}
                        · {localLeft >= 0 ? `${localLeft}d left` : "expired"}
                      </span>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt>Account seats</dt>
                  <dd className="mono">
                    {local.usedAccounts ?? "—"} / {local.maxAccounts ?? "—"}
                  </dd>
                </div>
                <div>
                  <dt>License</dt>
                  <dd>{local.skuName || local.kind || local.plan || "—"}</dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="lic-card-desc">
              {loading ? "Loading…" : "No local license data."}
            </p>
          )}
        </section>
      </div>

      {/* Tabs */}
      <div className="lic-tabs" role="tablist">
        {(
          [
            ["ips", "Server IPs"],
            ["licenses", "Licenses"],
            ["packages", "Packages"],
            ["events", "Events"],
            ["local", "Apply Key"]
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`lic-tab ${tab === k ? "is-active" : ""}`}
            onClick={() => setTab(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Server IPs / Installations ── */}
      {tab === "ips" ? (
        <section className="lic-card lic-registry">
          <div className="lic-card-hd-row">
            <div>
              <h3 className="lic-card-title">Installations</h3>
              <p className="lic-card-desc">
                All RealPanel installations registered with the licensing system.
              </p>
            </div>
          </div>

          <div className="lic-toolbar">
            <input
              type="search"
              className="lic-search"
              placeholder="Search owner, IP, hostname, install ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="lic-filters">
              {(
                [
                  ["all", "All"],
                  ["trial", "Trial"],
                  ["active", "Active"],
                  ["expired", "Expired"],
                  ["suspended", "Suspended"],
                  ["unassigned", "Unassigned"]
                ] as const
              ).map(([k, lab]) => (
                <button
                  key={k}
                  type="button"
                  className={`lic-filter ${statusFilter === k ? "is-active" : ""}`}
                  onClick={() => setStatusFilter(k)}
                >
                  {lab}
                </button>
              ))}
              <select
                className="lic-select"
                value={typeFilter}
                onChange={(e) =>
                  setTypeFilter(e.target.value as "all" | "vps" | "reseller")
                }
                aria-label="Type filter"
              >
                <option value="all">All types</option>
                <option value="vps">VPS</option>
                <option value="reseller">Reseller</option>
              </select>
            </div>
          </div>

          {/* Desktop table */}
          <div className="lic-table-wrap">
            <table className="lic-table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th>Server IP</th>
                  <th>Type</th>
                  <th>Package</th>
                  <th>Seats</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading && !installs.length ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="lic-skel-row">
                      <td colSpan={8}>
                        <div className="lic-skel" />
                      </td>
                    </tr>
                  ))
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="lic-empty">
                      {search || statusFilter !== "all" ? (
                        <>
                          <strong>No installations found</strong>
                          <span>
                            Try another owner, IP, hostname or package.
                          </span>
                        </>
                      ) : (
                        <>
                          <strong>No RealPanel installations registered</strong>
                          <span>
                            New panel installations will appear here automatically.
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((i) => {
                    const st = installStatus(i);
                    const left = daysLeft(i.expiresAt);
                    const used = i.usedAccounts ?? 0;
                    const max = i.maxAccounts;
                    const pct =
                      max && max > 0
                        ? Math.min(100, Math.round((used / max) * 100))
                        : 0;
                    const type =
                      i.typeLabel ||
                      (i.kind === "reseller"
                        ? "Reseller"
                        : i.packageId || i.kind
                          ? "VPS"
                          : "Unassigned");
                    const dupe = (ipDupes.get(i.serverIp) || 0) > 1;
                    return (
                      <tr key={i.id}>
                        <td>
                          <button
                            type="button"
                            className="lic-linkish"
                            onClick={() => void openDetail(i.id)}
                          >
                            <strong>
                              {i.ownerName || i.customerName || "—"}
                            </strong>
                          </button>
                          <div className="lic-sec mono">
                            {i.hostname || i.installId.slice(0, 10)}
                          </div>
                        </td>
                        <td>
                          <span className="mono lic-ip">
                            {i.serverIp}
                            <button
                              type="button"
                              className="lic-copy"
                              title="Copy IP"
                              onClick={() =>
                                void copyText(i.serverIp, "Server IP copied.")
                              }
                            >
                              ⎘
                            </button>
                          </span>
                          {dupe ? (
                            <div className="lic-sec lic-warn-text">
                              IP conflict
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <span
                            className={`lic-type ${
                              type === "Reseller"
                                ? "is-reseller"
                                : type === "Unassigned"
                                  ? "is-none"
                                  : "is-vps"
                            }`}
                          >
                            {type}
                          </span>
                        </td>
                        <td>
                          {i.packageName ? (
                            <>
                              <strong>{i.packageName}</strong>
                              <div className="lic-sec">
                                {i.maxAccounts != null
                                  ? `${i.maxAccounts.toLocaleString()} seats`
                                  : ""}
                              </div>
                            </>
                          ) : (
                            <>
                              <span className="faint">No package</span>
                              <div className="lic-sec">Trial</div>
                            </>
                          )}
                        </td>
                        <td>
                          {max != null ? (
                            <div className="lic-seats">
                              <span className="mono">
                                {used.toLocaleString()} / {max.toLocaleString()}
                              </span>
                              <div className="lic-seat-bar">
                                <div
                                  className="lic-seat-fill"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <div className="lic-sec">
                                {used} used ·{" "}
                                {Math.max(0, max - used).toLocaleString()}{" "}
                                available
                              </div>
                            </div>
                          ) : (
                            <span className="faint">—</span>
                          )}
                        </td>
                        <td>
                          <span className={`lic-badge ${st.cls}`}>
                            {st.label}
                          </span>
                          {left != null && left >= 0 ? (
                            <div className="lic-sec">{left}d remaining</div>
                          ) : null}
                        </td>
                        <td>
                          {left != null && left >= 0 ? (
                            <>
                              <strong
                                className={
                                  left <= 14 ? "lic-warn-text" : undefined
                                }
                              >
                                {left} days left
                              </strong>
                              <div className="lic-sec">
                                {formatDate(i.expiresAt)}
                              </div>
                            </>
                          ) : left != null && left < 0 ? (
                            <>
                              <strong className="lic-danger-text">
                                EXPIRED
                              </strong>
                              <div className="lic-sec">
                                {formatDate(i.expiresAt)}
                              </div>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="lic-actions-cell">
                          {!i.packageId ? (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => openAssign(i)}
                            >
                              Assign Package
                            </button>
                          ) : (
                            <div className="lic-menu-wrap">
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm lic-menu-btn"
                                aria-label="Actions"
                                onClick={() =>
                                  setMenuId((m) => (m === i.id ? null : i.id))
                                }
                              >
                                •••
                              </button>
                              {menuId === i.id ? (
                                <div className="lic-menu">
                                  <button
                                    type="button"
                                    onClick={() => void openDetail(i.id)}
                                  >
                                    View Details
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openAssign(i)}
                                  >
                                    Change Package
                                  </button>
                                  {i.licenseId ? (
                                    <button
                                      type="button"
                                      className="lic-menu-danger"
                                      onClick={() => {
                                        setMenuId(null);
                                        const lic = items.find(
                                          (l) => l.id === i.licenseId
                                        );
                                        if (lic) setRevokeTarget(lic);
                                      }}
                                    >
                                      Revoke License
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lic-mobile-list">
            {pageRows.map((i) => {
              const st = installStatus(i);
              const left = daysLeft(i.expiresAt);
              return (
                <article key={i.id} className="lic-m-card">
                  <div className="lic-m-hd">
                    <strong>
                      {i.ownerName || i.customerName || "—"}
                    </strong>
                    <span className={`lic-badge ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="mono lic-sec">{i.serverIp}</div>
                  <div className="lic-m-meta">
                    {i.typeLabel || i.kind || "—"}
                    {i.packageName ? ` · ${i.packageName}` : " · No package"}
                  </div>
                  <div className="lic-m-meta">
                    {i.maxAccounts != null
                      ? `${i.usedAccounts ?? 0} / ${i.maxAccounts} seats`
                      : "—"}
                    {left != null
                      ? ` · ${left >= 0 ? `${left}d left` : "expired"}`
                      : ""}
                  </div>
                  <div className="lic-m-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void openDetail(i.id)}
                    >
                      Details
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => openAssign(i)}
                    >
                      {i.packageId ? "Change" : "Assign"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {filteredInstalls.length > 0 ? (
            <div className="lic-pager">
              <span className="lic-sec">
                Showing {(pageSafe - 1) * pageSize + 1}–
                {Math.min(pageSafe * pageSize, filteredInstalls.length)} of{" "}
                {filteredInstalls.length} installations
              </span>
              <div className="lic-pager-controls">
                <select
                  className="lic-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={pageSafe <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="lic-sec">
                  {pageSafe} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={pageSafe >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}

          {/* Trial IP ledger — secondary */}
          <div className="lic-ledger">
            <h3 className="lic-card-title">Trial IP Ledger</h3>
            <p className="lic-card-desc">
              Public IPs that have previously consumed a free trial.
            </p>
            <p className="lic-policy-banner">
              A public IP that has already consumed a trial may require an
              assigned package/license before reinstalling.
            </p>
            <div className="lic-kpi-strip lic-kpi-compact">
              <div className="lic-kpi">
                <span className="lic-kpi-label">Unique Trial IPs</span>
                <span className="lic-kpi-value">{trialIps.length}</span>
              </div>
              <div className="lic-kpi">
                <span className="lic-kpi-label">Active Trials</span>
                <span className="lic-kpi-value">{summary?.trial ?? "—"}</span>
              </div>
            </div>
            <input
              type="search"
              className="lic-search"
              placeholder="Search IP…"
              value={ipSearch}
              onChange={(e) => setIpSearch(e.target.value)}
              style={{ marginBottom: 10, maxWidth: 280 }}
            />
            <div className="lic-table-wrap">
              <table className="lic-table lic-table-sm">
                <thead>
                  <tr>
                    <th>IP Address</th>
                    <th>Installation</th>
                    <th>First Seen</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrialIps.map((t) => (
                    <tr key={t.ip + (t.installId || "")}>
                      <td className="mono">
                        {t.ip}
                        <button
                          type="button"
                          className="lic-copy"
                          onClick={() =>
                            void copyText(t.ip, "Server IP copied.")
                          }
                        >
                          ⎘
                        </button>
                      </td>
                      <td className="mono faint" style={{ fontSize: 12 }}>
                        {t.installId?.slice(0, 12) || "—"}
                        {t.hostname ? (
                          <div className="lic-sec">{t.hostname}</div>
                        ) : null}
                      </td>
                      <td>{formatDate(t.firstSeenAt)}</td>
                      <td className="faint">{t.notes || "—"}</td>
                    </tr>
                  ))}
                  {!filteredTrialIps.length ? (
                    <tr>
                      <td colSpan={4} className="lic-empty">
                        No trial IPs recorded.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Licenses tab ── */}
      {tab === "licenses" ? (
        <div className="lic-split-stack">
          <section className="lic-card">
            <h3 className="lic-card-title">Issue license</h3>
            <p className="lic-card-desc">
              Create a paid/renewal key for a customer install.
            </p>
            <form className="lic-form" onSubmit={(e) => void onIssue(e)}>
              <label className="lic-field">
                <span>Customer name</span>
                <input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  placeholder="Acme Hosting"
                />
              </label>
              <label className="lic-field">
                <span>Email</span>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="billing@acme.test"
                />
              </label>
              <div className="lic-form-row">
                <label className="lic-field">
                  <span>Duration (days)</span>
                  <input
                    type="number"
                    min={1}
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value) || 365)}
                  />
                </label>
                <label className="lic-field">
                  <span>Max servers</span>
                  <input
                    type="number"
                    min={1}
                    value={maxServers}
                    onChange={(e) =>
                      setMaxServers(Number(e.target.value) || 1)
                    }
                  />
                </label>
              </div>
              <label className="lic-field">
                <span>Seat product (SKU)</span>
                <select
                  value={skuId}
                  onChange={(e) => {
                    setSkuId(e.target.value);
                    const s = skus.find((x) => x.id === e.target.value);
                    if (s) {
                      setMaxAccounts(s.maxAccounts);
                      setKind(s.kind);
                    }
                  }}
                >
                  <option value="">Custom seats…</option>
                  {skus.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.maxAccounts} seats · ${s.priceMonthly}/mo
                    </option>
                  ))}
                </select>
              </label>
              <label className="lic-field">
                <span>Max hosting accounts</span>
                <input
                  type="number"
                  min={1}
                  value={maxAccounts}
                  disabled={Boolean(skuId)}
                  onChange={(e) =>
                    setMaxAccounts(Number(e.target.value) || 1)
                  }
                />
              </label>
              <label className="lic-field">
                <span>Kind</span>
                <select
                  value={kind}
                  disabled={Boolean(skuId)}
                  onChange={(e) =>
                    setKind(e.target.value as "user" | "reseller")
                  }
                >
                  <option value="user">User / VPS license</option>
                  <option value="reseller">Reseller license</option>
                </select>
              </label>
              <label className="lic-field">
                <span>Notes</span>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Invoice #…"
                />
              </label>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || !customerName.trim()}
              >
                Issue license key
              </button>
            </form>
          </section>

          <section className="lic-card lic-grow">
            <h3 className="lic-card-title">All licenses</h3>
            <div className="lic-table-wrap">
              <table className="lic-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Key</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Seats</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <strong>{l.customerName || "—"}</strong>
                        <div className="lic-sec">
                          {l.customerEmail || l.plan}
                        </div>
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {maskKey(l.licenseKey)}
                        <button
                          type="button"
                          className="lic-copy"
                          onClick={() =>
                            void copyText(l.licenseKey, "License key copied.")
                          }
                        >
                          ⎘
                        </button>
                      </td>
                      <td>
                        <span
                          className={`lic-badge ${
                            l.status === "active"
                              ? "lic-badge-ok"
                              : l.status === "trial"
                                ? "lic-badge-warn"
                                : l.status === "revoked" ||
                                    l.status === "expired"
                                  ? "lic-badge-danger"
                                  : "lic-badge-muted"
                          }`}
                        >
                          {l.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <strong>
                          {l.daysRemaining != null
                            ? `${l.daysRemaining}d left`
                            : formatDate(l.expiresAt)}
                        </strong>
                        <div className="lic-sec">
                          {formatDate(l.expiresAt)}
                        </div>
                      </td>
                      <td className="mono">
                        {l.installCount ?? 0}/{l.maxServers}
                        {l.maxAccounts != null
                          ? ` · ${l.maxAccounts.toLocaleString()}`
                          : ""}
                      </td>
                      <td>
                        <div className="lic-inline-actions">
                          <input
                            type="number"
                            className="lic-renew-input"
                            min={1}
                            value={renewDays[l.id] ?? 30}
                            onChange={(e) =>
                              setRenewDays((prev) => ({
                                ...prev,
                                [l.id]: Number(e.target.value) || 30
                              }))
                            }
                            title="Days to add"
                          />
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={busy || l.status === "revoked"}
                            onClick={() => void onRenew(l.id)}
                          >
                            Renew
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm lic-danger-btn"
                            disabled={busy || l.status === "revoked"}
                            onClick={() => setRevokeTarget(l)}
                          >
                            Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!items.length ? (
                    <tr>
                      <td colSpan={6} className="lic-empty">
                        No licenses yet — first install creates a trial
                        automatically.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : null}

      {/* ── Packages (SKUs) ── */}
      {tab === "packages" ? (
        <section className="lic-card">
          <h3 className="lic-card-title">Seat products</h3>
          <p className="lic-card-desc">
            Commercial license SKUs: price + max accounts. Owner Packages (VPS /
            Reseller) are managed under Package → Owner Packages.
          </p>
          <div className="lic-pkg-grid">
            <div>
              <h4 className="lic-subhead">VPS packages</h4>
              <p className="lic-sec">For direct server / VPS owners</p>
              <ul className="lic-pkg-list">
                {vpsPackages.map((p) => (
                  <li key={p.id}>
                    <strong>{p.name}</strong>
                    <span className="mono">
                      {p.maxAccounts.toLocaleString()} seats
                      {p.priceMonthly ? ` · $${p.priceMonthly}/mo` : ""}
                    </span>
                  </li>
                ))}
                {!vpsPackages.length ? (
                  <li className="faint">No VPS packages yet</li>
                ) : null}
              </ul>
            </div>
            <div>
              <h4 className="lic-subhead">Reseller packages</h4>
              <p className="lic-sec">
                For hosting resellers managing customer accounts
              </p>
              <ul className="lic-pkg-list">
                {resellerPackages.map((p) => (
                  <li key={p.id}>
                    <strong>{p.name}</strong>
                    <span className="mono">
                      {p.maxAccounts.toLocaleString()} seats
                      {p.priceMonthly ? ` · $${p.priceMonthly}/mo` : ""}
                    </span>
                  </li>
                ))}
                {!resellerPackages.length ? (
                  <li className="faint">No reseller packages yet</li>
                ) : null}
              </ul>
            </div>
          </div>
          {skus.length ? (
            <div className="lic-table-wrap" style={{ marginTop: 16 }}>
              <table className="lic-table lic-table-sm">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Kind</th>
                    <th>Seats</th>
                    <th>Monthly</th>
                    <th>Yearly</th>
                  </tr>
                </thead>
                <tbody>
                  {skus.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <strong>{s.name}</strong>
                        <div className="lic-sec">{s.description}</div>
                      </td>
                      <td className="mono">{s.kind}</td>
                      <td className="mono">{s.maxAccounts}</td>
                      <td className="mono">
                        ${s.priceMonthly} {s.currency}
                      </td>
                      <td className="mono">
                        ${s.priceYearly} {s.currency}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* ── Events ── */}
      {tab === "events" ? (
        <section className="lic-card">
          <h3 className="lic-card-title">License Events</h3>
          <p className="lic-card-desc">
            Audit trail of trial starts, package assignments, renewals and
            revocations.
          </p>
          <div className="lic-table-wrap">
            <table className="lic-table lic-table-sm">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Installation / IP</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id}>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {ev.createdAt
                        ? ev.createdAt.slice(0, 19).replace("T", " ")
                        : "—"}
                    </td>
                    <td>{ev.actor || "system"}</td>
                    <td>
                      <code className="lic-event-code">{ev.event}</code>
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {ev.ip || ev.installId?.slice(0, 12) || "—"}
                    </td>
                    <td className="faint">{ev.detail || "—"}</td>
                  </tr>
                ))}
                {!events.length ? (
                  <tr>
                    <td colSpan={5} className="lic-empty">
                      No events yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* ── Apply key ── */}
      {tab === "local" ? (
        <section className="lic-card" style={{ maxWidth: 480 }}>
          <h3 className="lic-card-title">Apply license key</h3>
          <p className="lic-card-desc">
            Bind a paid license key to this server (this node).
          </p>
          <form className="lic-form" onSubmit={(e) => void onApplyKey(e)}>
            <label className="lic-field">
              <span>License key</span>
              <input
                className="mono"
                value={applyKey}
                onChange={(e) => setApplyKey(e.target.value)}
                placeholder="RP-…"
                required
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy || !applyKey.trim()}
            >
              Apply key
            </button>
          </form>
        </section>
      ) : null}

      {/* Assign package drawer */}
      {assignId && assignInstall ? (
        <div
          className="lic-drawer-bg"
          onClick={() => setAssignId(null)}
          role="presentation"
        >
          <aside
            className="lic-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Assign license package"
          >
            <header className="lic-drawer-hd">
              <div>
                <h3>Assign License Package</h3>
                <p className="lic-sec">
                  {assignInstall.ownerName ||
                    assignInstall.customerName ||
                    "Installation"}{" "}
                  · <span className="mono">{assignInstall.serverIp}</span>
                </p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setAssignId(null)}
              >
                Close
              </button>
            </header>
            <form
              className="lic-form"
              onSubmit={(e) => void onAssignPackage(e)}
            >
              <div className="lic-type-pick">
                <span className="lic-field-label">License type</span>
                <div className="lic-type-cards">
                  <div className="lic-type-card">
                    <strong>VPS Package</strong>
                    <span>For direct server/VPS owners</span>
                  </div>
                  <div className="lic-type-card">
                    <strong>Reseller Package</strong>
                    <span>For hosting resellers managing customers</span>
                  </div>
                </div>
              </div>
              <label className="lic-field">
                <span>Package</span>
                <select
                  value={assignPackageId}
                  onChange={(e) => setAssignPackageId(e.target.value)}
                  required
                >
                  <option value="">— Select package —</option>
                  <optgroup label="VPS Packages">
                    {vpsPackages.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {p.maxAccounts.toLocaleString()} seats
                        {p.priceMonthly ? ` · $${p.priceMonthly}/mo` : ""}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Reseller Packages">
                    {resellerPackages.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {p.maxAccounts.toLocaleString()} seats
                        {p.priceMonthly ? ` · $${p.priceMonthly}/mo` : ""}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </label>
              {selectedPkg ? (
                <div className="lic-pkg-preview">
                  <strong>{selectedPkg.name}</strong>
                  <div>
                    {selectedPkg.maxAccounts.toLocaleString()} account seats
                  </div>
                  <div className="lic-sec">
                    {selectedPkg.priceMonthly
                      ? `$${selectedPkg.priceMonthly}/mo`
                      : ""}
                    {selectedPkg.priceYearly
                      ? ` · $${selectedPkg.priceYearly}/yr`
                      : ""}
                    {" · "}
                    {selectedPkg.planKind === "reseller" ? "Reseller" : "VPS"}
                  </div>
                </div>
              ) : null}
              <label className="lic-field">
                <span>License term (days)</span>
                <input
                  type="number"
                  min={1}
                  value={assignDays}
                  onChange={(e) =>
                    setAssignDays(Number(e.target.value) || 365)
                  }
                />
              </label>
              <label className="lic-field">
                <span>Customer name</span>
                <input
                  value={assignCustomer}
                  onChange={(e) => setAssignCustomer(e.target.value)}
                />
              </label>
              <label className="lic-field">
                <span>Customer email</span>
                <input
                  value={assignEmail}
                  onChange={(e) => setAssignEmail(e.target.value)}
                />
              </label>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || !assignPackageId}
              >
                {busy ? "Assigning…" : "Assign Package"}
              </button>
            </form>
          </aside>
        </div>
      ) : null}

      {/* Detail drawer */}
      {detail ? (
        <div
          className="lic-drawer-bg"
          onClick={() => setDetail(null)}
          role="presentation"
        >
          <aside
            className="lic-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Installation details"
          >
            <header className="lic-drawer-hd">
              <div>
                <h3>Installation Details</h3>
                <p className="lic-sec mono">{detail.serverIp}</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setDetail(null)}
              >
                Close
              </button>
            </header>
            <dl className="lic-detail-dl">
              <div>
                <dt>Owner</dt>
                <dd>{detail.ownerName || detail.customerName || "—"}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{detail.ownerEmail || detail.customerEmail || "—"}</dd>
              </div>
              <div>
                <dt>Server IP</dt>
                <dd className="mono">
                  {detail.serverIp}
                  <button
                    type="button"
                    className="lic-copy"
                    onClick={() =>
                      void copyText(detail.serverIp, "Server IP copied.")
                    }
                  >
                    ⎘
                  </button>
                </dd>
              </div>
              <div>
                <dt>Hostname</dt>
                <dd>{detail.hostname || "—"}</dd>
              </div>
              <div>
                <dt>Install ID</dt>
                <dd className="mono" style={{ fontSize: 12 }}>
                  {detail.installId}
                </dd>
              </div>
              <div>
                <dt>License type</dt>
                <dd>{detail.typeLabel || detail.kind || "—"}</dd>
              </div>
              <div>
                <dt>Package</dt>
                <dd>{detail.packageName || "No package (trial)"}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span className={`lic-badge ${installStatus(detail).cls}`}>
                    {installStatus(detail).label}
                  </span>
                </dd>
              </div>
              <div>
                <dt>Issued</dt>
                <dd>
                  {formatDate(detail.activatedAt || detail.firstSeenAt)}
                </dd>
              </div>
              <div>
                <dt>Expires</dt>
                <dd>{formatDate(detail.expiresAt)}</dd>
              </div>
              <div>
                <dt>Seats</dt>
                <dd className="mono">
                  {detail.maxAccounts != null
                    ? `${detail.usedAccounts ?? 0} / ${detail.maxAccounts}`
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>License key</dt>
                <dd className="mono" style={{ fontSize: 12 }}>
                  {maskKey(detail.licenseKey || detail.licenseKeyHint)}
                </dd>
              </div>
            </dl>
            <div className="lic-drawer-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => openAssign(detail)}
              >
                Change Package
              </button>
              {detail.licenseId ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm lic-danger-btn"
                  onClick={() => {
                    const lic = items.find((l) => l.id === detail.licenseId);
                    if (lic) {
                      setDetail(null);
                      setRevokeTarget(lic);
                    }
                  }}
                >
                  Revoke
                </button>
              ) : null}
            </div>
          </aside>
        </div>
      ) : null}

      {/* Revoke confirm */}
      {revokeTarget ? (
        <div className="lic-modal-bg" role="presentation">
          <div className="lic-modal" role="alertdialog">
            <h3>Revoke License?</h3>
            <p>
              This will invalidate the license associated with{" "}
              <strong>{revokeTarget.customerName || "this customer"}</strong>.
            </p>
            <dl className="lic-meta-grid">
              <div>
                <dt>Current status</dt>
                <dd>{revokeTarget.status}</dd>
              </div>
              <div>
                <dt>Package</dt>
                <dd>{revokeTarget.packageName || revokeTarget.plan}</dd>
              </div>
              <div>
                <dt>Seats</dt>
                <dd className="mono">{revokeTarget.maxAccounts ?? "—"}</dd>
              </div>
            </dl>
            <div className="lic-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setRevokeTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary lic-danger-solid"
                disabled={busy}
                onClick={() => void onRevokeConfirm()}
              >
                Revoke License
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
